import React, { useRef, useState } from "react";
import classNames from "classnames";
import {
  UploaderEmptyState,
  UploaderLoadingState,
  UploaderSingleMode,
  UploaderMultipleMode,
  DefaultDocument
} from "./UploaderStates";
import Icon, { Size } from "../icon/icon";

export type { DefaultDocument };

type DocumentUploaderProps = {
  id?: string;
  title?: string;
  subtitle?: string;
  hint?: string;
  error?: string;
  loadingTitle?: string;
  accept?: string;
  disabled?: boolean;
  required?: boolean;
  uploadedDocument?: DefaultDocument | null;
  isDocumentSelected?: boolean;
  isMultiple?: boolean;
  formName?: string;
  onFileUpload: (file: File) => void;
  onDocumentSelect?: (document: DefaultDocument) => void;
  isLoading?: boolean;
};

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

export default function DocumentUploader(props: DocumentUploaderProps) {
  const styles = require("./DocumentUploader.module.scss");
  const inputRef = useRef<HTMLInputElement>(null);
  const [internalError, setInternalError] = useState<string>("");

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return "";
    const thresh = 1024;
    if (bytes < thresh) return `${bytes} B`;
    const units = ["KB", "MB", "GB", "TB"];
    let u = -1;
    let b = bytes;
    do {
      b /= thresh;
      u++;
    } while (b >= thresh && u < units.length - 1);
    return `${b.toFixed(1)} ${units[u]}`;
  };

  const isValidFileType = (file: File, accept?: string): boolean => {
    if (!accept) return true;
    
    const fileExt = file.name.split('.').pop()?.toLowerCase() || '';
    const mimeType = file.type;
    
    return accept.split(',').some(pattern => {
      const type = pattern.trim().toLowerCase();
      
      if (type.startsWith('.')) {
        return file.name.toLowerCase().endsWith(type);
      }
      if (!type.includes('/')) {
        return fileExt === type;
      }
      
      if (type.endsWith('/*')) {
        return mimeType.startsWith(type.split('/*')[0] + '/');
      }
      
      return mimeType === type;
    });
  };

  const openFilePicker = () => inputRef.current?.click();

  const validateAndUploadFile = (file: File) => {
    setInternalError("");
    
    const isValidType = isValidFileType(file, props.accept);
    const isValidSize = file.size <= MAX_FILE_SIZE;
    
    if (isValidType && isValidSize) {
      props.onFileUpload(file);
    } else {
      const message = props.uploadedDocument 
        ? "To replace, please select a valid format and size document" 
        : "Please select a valid format and size document";
      setInternalError(message);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      validateAndUploadFile(selectedFile);
    }
    e.target.value = '';
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (props.disabled) return;
    
    const file = e.dataTransfer.files?.[0];
    if (file) {
      validateAndUploadFile(file);
    }
  };

  const displayError = internalError || props.error;

  const renderHintSection = () => {
    if (!props.hint && !displayError) return null;
    
    return (
      <div className={styles.hint}>
        {displayError && <Icon name="warning" size={Size.Medium} color="flamingoMidDark" />}
        {props.hint && <Icon name="moreInfo" size={Size.Medium} color="silverSandDark" />}
        <span className={classNames(styles.hintText, displayError && styles.hintTextError)}>
          {displayError || props.hint}
        </span>
      </div>
    );
  };

  const renderFileInput = () => (
    <input
      ref={inputRef}
      type="file"
      id={props.id}
      disabled={props.disabled}
      required={props.required}
      onChange={handleFileChange}
      className={styles.hiddenInput}
      accept={props.accept}
    />
  );

  const renderUploaderState = () => {
    if (props.isLoading) {
      return (
        <UploaderLoadingState
          loadingTitle={props.loadingTitle || "Uploading..."}
        />
      );
    }

    if (!props.uploadedDocument) {
      return (
        <UploaderEmptyState
          title={props.title || "Click here to upload"}
          subtitle={props.subtitle}
          disabled={props.disabled}
          error={!!displayError}
          onClick={openFilePicker}
          onDrop={handleDrop}
        />
      );
    }

    if (props.isMultiple) {
      return (
        <UploaderMultipleMode
          document={props.uploadedDocument}
          isSelected={props.isDocumentSelected}
          formName={props.formName}
          onSelect={() => props.onDocumentSelect?.(props.uploadedDocument)}
          onReplace={openFilePicker}
        />
      );
    }

    return (
      <UploaderSingleMode
        document={props.uploadedDocument}
        disabled={props.disabled}
        formatFileSize={formatFileSize}
        onClick={openFilePicker}
        onDrop={handleDrop}
      />
    );
  };

  return (
    <div className={styles.container}>
      {renderUploaderState()}
      {renderHintSection()}
      {renderFileInput()}
    </div>
  );
}
